import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { SettingService } from 'src/app/core/service/setting.service';
import { State } from '@progress/kendo-data-query';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { EventListService } from 'src/app/core/service/grid-data/grid-data-event.service';
import { EventData } from 'src/app/event/model/event';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { FilterService } from 'src/app/core/filter/filter.service';
import { Filter } from 'src/app/core/filter/filter';

@Component({
  standalone: false,
  selector: 'app-event-list',
  templateUrl: './event-list.component.html',
  styleUrls: ['./event-list.component.scss']
})
export class EventListComponent implements OnInit, OnDestroy
{
  @Input() public source: string;
  @Input() public memberId: number;

  @Output() public total = new EventEmitter<number>();

  public events = [];
  public filterApplied = false;
  public eventTypes: Array<{ text: string; value: string; icon: string }>;
  public encounterTypes: Array<{ text: string; value: string }>;

  private limitEvents = 0;
  private state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  private members = new Set<string>();
  private totalMembers = 0;
  private currentUser = this.authenticationService.currentUser;
  private listSubscription: Subscription;
  private routeParamSubscription: Subscription;
  private dateLimit: Date;

  constructor(
    private eventListService: EventListService,
    private settingService: SettingService,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private filterService: FilterService
  ) { }

  public ngOnInit(): void
  {
    this.eventTypes = this.eventListService.eventTypes;
    this.encounterTypes = this.eventListService.encounterTypes;
    this.listSubscription = this.eventListService.subscribe({
      next: (value: EventData) =>
      {
        this.totalMembers = 0;
        this.members = new Set<string>();
        // Preprocessing data before it's passed to the Kendo Grid.
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.eventDateTime = new Date(d.eventDateTime);
            d.eventTypeIcon = this.eventListService.getEventTypeIcon(d.eventType);
            d.eventTypeText = this.eventListService.getEventTypeText(d.eventType);
            d.encounterTypeText = this.eventListService.getEncounterTypeText(d.encounterType);
            if (d.referral !== undefined && d.referral !== null && d.referral.date)
            {
              d.referral.date = new Date(d.referral.date);
            }
            if (!this.members.has(d.memberName))
            {
              this.members.add(d.memberName);
              this.totalMembers++;
            }
          }
          );
          this.total.emit(this.totalMembers);
          this.events = value.data;
        }
      }
    });

    this.routeParamSubscription = this.route.params.subscribe({
      next: () =>
      {
        this.getEvents();
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
    if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
  }

  public getAppointmentStatusText(appointmentStatus: string): string
  {
    const displayText = this.eventListService.getAppointmentStatusText(appointmentStatus);
    return `(${displayText})`;
  }

  private getFilterScreen()
  {
    const screen = (this.source === 'dashboard') ? 'COORDINATOR_DASHBOARD' : (this.source === 'patientSummary') ? 'PATIENT_SUMMARY' : '';
    this.filterService.getFilterByScreen('Event', screen).subscribe({
      next: (data: Filter) =>
      {
        if (data !== null)
        {
          const filterState = JSON.parse(data.state);
          if (filterState !== null)
          {
            filterState.filter.filters.forEach(f => this.state.filter.filters.push(f));
          }
        }
        this.eventListService.queryByStateAndSource(this.state, this.source);
        this.filterApplied = true;
      }
    });
  }

  private getEvents()
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.dateLimit = new Date();
        if (this.source === 'dashboard')
        {
          this.limitEvents = sessionSetting.noticeEventsHoursFilter !== undefined ? sessionSetting.noticeEventsHoursFilter : 24;
          this.dateLimit.setHours(this.dateLimit.getHours() - this.limitEvents);
          this.state = {
            filter: {
              filters: [
                { field: 'user', operator: 'eq', value: this.currentUser.userName },
                { field: 'eventDateTime', operator: 'gte', value: this.dateLimit !== undefined ? this.dateLimit : new Date() }
              ], logic: 'and'
            }, group: [], skip: 0, sort: [], take: 99999
          };
        }
        else if (this.source === 'patientSummary')
        {
          this.limitEvents = sessionSetting.eventPastDaysLimit !== undefined ? sessionSetting.eventPastDaysLimit : 30;
          this.dateLimit.setHours(this.dateLimit.getHours() - (this.limitEvents * 24));
          this.state = {
            filter: {
              filters: [
                { field: 'memberId', operator: 'eq', value: this.memberId },
                { field: 'eventDateTime', operator: 'gte', value: this.dateLimit !== undefined ? this.dateLimit : new Date() }
              ], logic: 'and'
            }, group: [], skip: 0, sort: [], take: 99999
          };
        }
        this.getFilterScreen();
      }
    });
  }
}
